import type {
  ContentLocation,
  ContentReference,
  Direction,
  NewEvent,
  SharedContentReference,
  TurnMethod,
} from '@ilteducation/activity-stream-types';
import { v4 as uuidv4 } from 'uuid';
import { detect } from 'detect-browser';
import { createCFIWithTemporalOffset } from './history';
import { Api, AppContext, UserInformation } from '../types';
import { sendBookReaderEventAnalytics } from '../composables/use-send-analytics';

const browser = detect();
const timestamp = () => new Date().toISOString();

type SessionInfo = {
  id: string;
  application: string;
};

let isEnabled = true;
export const setIsReadSessionEnabled = (izEnabled: boolean) => {
  isEnabled = izEnabled;
};

let sessionInfo: SessionInfo | null = null;
let api: Api | null = null;

const sendSessionEvent = (factory: (sessionInfo: SessionInfo) => NewEvent) => {
  if (sessionInfo === null) {
    console.error("Can't send event, the session has not be initialised!");
    return;
  }
  if (api === null) {
    console.error("Can't send event, the api has not be initialised!");
    return;
  }

  const event = factory(sessionInfo);
  const shouldFlush =
    event.type === 'ContentSessionEndedEventV1' ||
    event.type === 'PageTurnedEventV1' ||
    event.type === 'GoToLocationEventV1' ||
    event.type === 'GoToBookmarkEventV1';

  if (!isEnabled) {
    console.debug(`Skipped sending session event:`, event);
    return;
  }

  if (api.postActivityStreamEvents) {
    api.postActivityStreamEvents([event], shouldFlush);
  }
};

export const contentSessionStarted = (
  _api: Api,
  application: string,
  contentId: string,
  contentType: ContentReference['type'],
  userInformation: UserInformation,
  appContext?: AppContext,
  sharedContent?: SharedContentReference,
): string => {
  if (sessionInfo !== null) {
    console.debug(`Has unexpected active session ${JSON.stringify(sessionInfo)}`);
  }

  api = _api;
  sessionInfo = {
    id: uuidv4(),
    application,
  };

  const { deviceInfo, appInfo } = appContext ?? {
    deviceInfo: {
      type: 'Browser',
      name: browser?.name || 'unknown',
      version: browser?.version || '0.0.0',
    },
    appInfo: {
      type: 'Web',
      version: process.env.PACKAGE_VERSION || '0',
      language: userInformation.locale,
    },
  };

  if (api.postActivityStreamEvents) {
    api.postActivityStreamEvents(
      [
        {
          partitionKey: sessionInfo.id,
          sourceApplication: sessionInfo.application,
          type: 'ContentSessionStartedEventV1',
          payload: {
            sessionId: sessionInfo.id,
            // @ts-ignore - Why is this Date and not string? - https://ilteducation.slack.com/archives/C038B1BJA4R/p1737377233648779
            timestamp: timestamp(),
            contentReference: { type: contentType, id: contentId } as ContentReference,
            userIdentity: userInformation.userIdentity,
            deviceInfo,
            appInfo,
            service: userInformation.subscriptionType,
            ...(sharedContent && { sharedContent }),
          },
        },
      ],
      true,
    );
  }

  return sessionInfo.id;
};

export const contentSessionEnded = () => {
  // BookPlayer, AudioOnly and Reader each register their own pagehide listener.
  if (sessionInfo === null) {
    return;
  }

  sendBookReaderEventAnalytics('BOOK_CLOSED');
  sendSessionEvent(({ id, application }) => ({
    partitionKey: id,
    sourceApplication: application,
    type: 'ContentSessionEndedEventV1',

    payload: {
      sessionId: id,
      // @ts-ignore - Why is this Date and not string? - https://ilteducation.slack.com/archives/C038B1BJA4R/p1737377233648779
      timestamp: timestamp(),
    },
  }));

  sessionInfo = null;
};

export const pageTurned = (
  direction: Direction,
  method: TurnMethod,
  location: ContentLocation,
  isFlowLayout: boolean,
) =>
  sendSessionEvent(({ id, application }) => ({
    partitionKey: id,
    sourceApplication: application,
    type: 'PageTurnedEventV1',
    payload: {
      sessionId: id,
      // @ts-ignore - Why is this Date and not string? - https://ilteducation.slack.com/archives/C038B1BJA4R/p1737377233648779
      timestamp: timestamp(),
      direction,
      method,
      location,
      isFlowLayout,
    },
  }));

export const audioSlicePlayed = (
  startSeconds: number,
  endSeconds: number,
  location: string,
  track: string,
  language: string,
) => {
  const cfiTempOffset = createCFIWithTemporalOffset(location, endSeconds);

  sendSessionEvent(({ id, application }) => ({
    partitionKey: id,
    sourceApplication: application,
    type: 'AudioSlicePlayedEventV1',
    payload: {
      sessionId: id,
      // @ts-ignore - Why is this Date and not string? - https://ilteducation.slack.com/archives/C038B1BJA4R/p1737377233648779
      timestamp: timestamp(),
      startSeconds,
      endSeconds,
      location: cfiTempOffset,
      track,
      language,
    },
  }));
};

export const goToLocation = (location: ContentLocation) =>
  sendSessionEvent(({ id, application }) => ({
    partitionKey: id,
    sourceApplication: application,
    type: 'GoToLocationEventV1',
    payload: {
      sessionId: id,
      // @ts-ignore - Why is this Date and not string? - https://ilteducation.slack.com/archives/C038B1BJA4R/p1737377233648779
      timestamp: timestamp(),
      location,
    },
  }));
